import { useEffect } from 'react';
import Config from './Config';
import { GetTokenForHeader } from './CommonHelper';

// Only authenticated activity; never send query strings, payment details or shipment tokens.
export default function useCheckoutActivity(stage, userId, pagePath = '/checkout', onSessionRequired) {
    useEffect(() => {
        if (!Number.isInteger(Number(userId)) || Number(userId) <= 0 || !stage || !window.crypto?.getRandomValues) return;
        const bytes = window.crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        const visitId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        const url = new URL('api/customer-checkout-activity', Config.ADMIN_BASE_URL).href;
        let stopped = false, busy = false, token = '', request, sequence = 0, sessionReported = false;
        const requireSession = () => {
            if (!sessionReported && !stopped) { sessionReported = true; onSessionRequired?.(); }
        };
        const send = (isVisible, options = {}) => window.fetch(url, {
            method: 'POST', credentials: 'omit', cache: 'no-store',
            headers: { 'Content-Type': 'application/json', Token: token },
            body: JSON.stringify({ visitId, stage, pagePath, isVisible, sequence: ++sequence }), ...options
        });
        const leave = () => {
            request?.abort();
            if (token) send(false, { keepalive: true }).catch(() => {});
        };
        const ping = async () => {
            if (stopped || busy || document.visibilityState !== 'visible') return;
            busy = true;
            let timeout;
            try {
                token = await GetTokenForHeader();
                if (stopped) return;
                if (!token) { requireSession(); return; }
                request = new window.AbortController();
                timeout = window.setTimeout(() => request.abort(), 8000);
                if (document.visibilityState === 'visible') {
                    const response = await send(true, { signal: request.signal });
                    if (response.status === 401) requireSession();
                    else if (!response.ok) throw new Error('Checkout activity request failed');
                    else sessionReported = false;
                }
            } catch { /* Activity reporting must never interrupt shopping. */ }
            finally { window.clearTimeout(timeout); busy = false; }
        };
        const visibility = () => document.visibilityState === 'visible' ? ping() : leave();
        ping();
        const timer = window.setInterval(ping, 30000);
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('pagehide', leave);
        return () => {
            stopped = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('pagehide', leave);
            leave();
        };
    }, [stage, userId, pagePath, onSessionRequired]);
}
